
import Vue from 'vue';
import HelpCenterAmbientBg from '@/views/HelpCenter/HelpCenterAmbientBg.vue';
import HelpCenterFaqs from '@/views/HelpCenter/HelpCenterFaqs.vue';
import { mapActions, mapState } from 'vuex';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { functions } from '@/firebase';
import { analytics, EVENTS } from '@/services/analytics';
import {
	openSupportChat,
	getSupportChatErrorMessage,
	buildSupportMailtoHref,
	SUPPORT_CHAT_SOURCE,
	SupportChatFailure,
	SupportChatIdTokenResponse,
} from '@/utils/supportChat';

const AMETHYST_ICON: string = 'var(--amethyst-800)';
const VISTA_ICON: string = 'var(--vista-blue-600)';

interface HelpAction {
	key: string;
	icon: string;
	iconColor: string;
	title: string;
	description: string;
	onClick: () => void;
}

export default Vue.extend({
	name: 'HelpCenter',
	metaInfo: {
		title: 'Help Center | RunDiffusion',
	},
	data () {
		return {
			isOpeningChatSupportWidget: false,
			chatSupportFailureReason: null as SupportChatFailure,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'team',
			'isAdminProxyingUser',
		]),
		actions (): HelpAction[] {
			return [
				{
					key: 'plan',
					icon: 'mdi-crown-outline',
					iconColor: AMETHYST_ICON,
					title: 'Manage My Plan',
					description: 'Change your subscription, update billing, and view invoices.',
					onClick: this.onManagePlanClick,
				},
				{
					key: 'masterclass',
					icon: 'mdi-school-outline',
					iconColor: VISTA_ICON,
					title: 'Open-Source Apps Masterclass',
					description: 'Full lesson series on the open-source applications.',
					onClick: this.onMasterclassClick,
				},
				{
					key: 'tutorials',
					icon: 'mdi-lightbulb-on-outline',
					iconColor: VISTA_ICON,
					title: 'Guides & Tutorials',
					description: 'Step-by-step walkthroughs for the tools on the platform.',
					onClick: this.onTutorialsClick,
				},
				{
					key: 'feature-request',
					icon: 'mdi-message-outline',
					iconColor: AMETHYST_ICON,
					title: 'Feature Request',
					description: 'Tell us what to build next. Every request gets read.',
					onClick: this.onFeatureRequestClick,
				},
			];
		},
		chatSupportErrorMessage (): string {
			return this.chatSupportFailureReason
				? getSupportChatErrorMessage(this.chatSupportFailureReason)
				: '';
		},
		chatSupportMailtoHref (): string {
			return buildSupportMailtoHref(this.chatSupportFailureReason, this._get(this.user, 'email', null));
		},
	},
	created () {
		// Direct "I'm stuck" signal. Especially valuable when correlated with
		// referring route and time-since-last-action via PostHog sessions.
		analytics.track(EVENTS.HELP_CENTER_VIEWED, {
			referrer_route: this._get(this.$route, 'redirectedFrom', null),
		});
	},
	methods: {
		...mapActions([
			'setUpsellDialog',
			'updateSnackbar',
		]),
		goTo (routeName: string) {
			this.routerPush(this.$route, this.$router, { name: routeName });
		},
		onManagePlanClick () {
			if (this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER && this.user?.clubOffer?.isFreemium) {
				this.goTo(ROUTER.RUNNITS_SUBSCRIPTIONS);
			} else {
				this.setUpsellDialog({
					runnitPlanDetailsData: {
						open: true,
					},
				});
			}
		},
		onMasterclassClick () {
			window.open('https://www.rundiffusion.com/open-source-masterclass', '_blank');
		},
		onTutorialsClick () {
			window.open('https://learn.rundiffusion.com/', '_blank');
		},
		onFeatureRequestClick () {
			window.open('https://discord.com/channels/1034587473512247348/1051569693086982264', '_blank');
		},
		async onStartChatSupport () {
			this.chatSupportFailureReason = null;
			this.isOpeningChatSupportWidget = true;

			const result = await openSupportChat({
				source: SUPPORT_CHAT_SOURCE.HELP_CENTER,
				email: this._get(this.user, 'email', null),
				teamId: this.team ? this.team.id : null,
				isAdminProxyingUser: this.isAdminProxyingUser,
				createIdToken: async (teamId: string): Promise<SupportChatIdTokenResponse> => (
					(await functions.httpsCallable('getSupportChatIdToken')({ teamId })).data
				),
			});

			this.isOpeningChatSupportWidget = false;
			if (result.ok) return;

			this.chatSupportFailureReason = result.reason;
			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message: getSupportChatErrorMessage(result.reason),
				show: true,
				timeout: 20000,
			});
		},
	},
	components: {
		HelpCenterAmbientBg,
		HelpCenterFaqs,
	},
});
